import type {
  CreateSoilSightProjectBody,
  CreateSoilSightProjectResponse,
  DroppedMainCrop,
  ReplaceCropYearsBody,
  ReplaceCropYearsResponse,
  SoilSightScenario,
  UpdateSettingsRefinementsBody,
} from "models";

import { CropYear } from "pages/app/scenario-analysis/types/scenario";
import { pruneEmptyCropYears } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { padCropYearsToMinimum } from "pages/app/scenario-analysis/utils/padSoilSightCropYears";
import { toApiCropYear } from "pages/app/scenario-analysis/utils/scenarioApiMappers";

export type PersistSoilSightFieldImportDeps = {
  getStoredSoilSightProjectId: () => string | null;
  setStoredSoilSightProjectId: (projectId: string) => void;
  createProject: (body: CreateSoilSightProjectBody) => Promise<CreateSoilSightProjectResponse>;
  listSoilSightScenarios: (soilSightProjectId: string) => Promise<SoilSightScenario[]>;
  replaceCropYears: (args: { scenarioId: string; body: ReplaceCropYearsBody }) => Promise<ReplaceCropYearsResponse>;
  updateRefinements: (args: { soilSightProjectId: string; body: UpdateSettingsRefinementsBody }) => Promise<unknown>;
};

export type PersistSoilSightFieldImportArgs = PersistSoilSightFieldImportDeps & {
  klimFarmId: string;
  fieldId: string;
  cropYears: CropYear[];
  locationRegion: string;
  referenceName?: string;
  projectionYears?: number;
  name?: string;
  /** Skips the scenario-list fetch when the caller already knows the reference scenario (e.g. repeated autosaves). */
  knownReferenceScenarioId?: string | null;
};

export type PersistSoilSightFieldImportResult = {
  soilSightProjectId: string;
  referenceScenarioId: string;
  /** The project's scenarios as fetched during the persist; null when the fetch was skipped via knownReferenceScenarioId. */
  scenarios: SoilSightScenario[] | null;
  cropYears: CropYear[];
  droppedMainCrops: DroppedMainCrop[];
};

/** Persists field rotation to Soil Sight (create project if needed, replace reference crop years, set field refinement). */
export const persistSoilSightFieldImport = async ({
  klimFarmId,
  fieldId,
  cropYears,
  locationRegion,
  referenceName,
  projectionYears,
  name,
  knownReferenceScenarioId,
  getStoredSoilSightProjectId,
  setStoredSoilSightProjectId,
  createProject,
  listSoilSightScenarios,
  replaceCropYears,
  updateRefinements,
}: PersistSoilSightFieldImportArgs): Promise<PersistSoilSightFieldImportResult> => {
  let soilSightProjectId = getStoredSoilSightProjectId();

  if (!soilSightProjectId) {
    const created = await createProject({
      farmId: klimFarmId,
      locationRegion,
      referenceName,
      projectionYears,
      name,
    });
    soilSightProjectId = created.soilSightProjectId;
    setStoredSoilSightProjectId(soilSightProjectId);
  }

  let referenceScenarioId = knownReferenceScenarioId ?? null;
  let scenarios: SoilSightScenario[] | null = null;
  if (!referenceScenarioId) {
    scenarios = await listSoilSightScenarios(soilSightProjectId);
    referenceScenarioId = (scenarios.find((scenario) => scenario.isReference) ?? scenarios[0])?.id ?? null;
  }
  if (!referenceScenarioId) {
    throw new Error("No reference scenario found");
  }

  // Strip leftover blank rows (e.g. from onboarding's default scaffold) before persisting — with a
  // minimum of one crop-year now allowed, they shouldn't linger as incomplete rows on the new
  // scenario. Pad afterward in case pruning (or the source data) left fewer than the minimum.
  const cropYearsForPersist = padCropYearsToMinimum(pruneEmptyCropYears(cropYears));

  const [replaceResult] = await Promise.all([
    replaceCropYears({
      scenarioId: referenceScenarioId,
      body: { cropYears: cropYearsForPersist.map((year, index) => toApiCropYear(year, index + 1)) },
    }),
    updateRefinements({ soilSightProjectId, body: { fieldId } }),
  ]);

  // Defensive against cross-repo sequencing: a missing response or droppedMainCrops means "nothing dropped".
  return { soilSightProjectId, referenceScenarioId, scenarios, cropYears: cropYearsForPersist, droppedMainCrops: replaceResult?.droppedMainCrops ?? [] };
};
